import { ChevronRightIcon } from '@heroicons/react/20/solid';
import React from 'react';

import { ChevronRotate, ExpandContent } from './Animations';
import { cn } from './cn';
import { Loader } from './Loader';

export interface ListRowColumn<T = any> {
  key: string;
  name?: string;
  fieldName?: string;
  minWidth?: number;
  maxWidth?: number;
  data?: {
    headerFieldName?: string;
    listCellProps?: {
      onRenderHeader?: (item: T, colIndex?: number, column?: ListRowColumn<T>, rowIndex?: number) => React.ReactNode;
      onRenderText?: (item: T, colIndex?: number, column?: ListRowColumn<T>, rowIndex?: number) => React.ReactNode;
      emptyMessage?: string;
      styles?: { root?: React.CSSProperties };
    };
  };
}

export interface ListRowProps<T extends Record<string, any> = Record<string, any>> {
  data: T;
  columns: ListRowColumn<T>[];
  contents?: React.ReactNode;
  collapsed?: boolean;
  hasOpenClose?: boolean;
  hasLeftOpenClose?: boolean;
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  onOpen?: (data: T, open: boolean) => void;
  onClick?: (e: React.MouseEvent) => void;
}

function ListRowCell<T extends Record<string, any>>({
  column,
  data,
  colIndex,
  rowIndex,
}: {
  column: ListRowColumn<T>;
  data: T;
  colIndex: number;
  rowIndex?: number;
}) {
  const cellProps = column.data?.listCellProps;
  const headerFieldName = column.data?.headerFieldName;
  const headerValue = headerFieldName ? data[headerFieldName] : undefined;
  const textValue = column.fieldName ? data[column.fieldName] : undefined;

  const headerContent = cellProps?.onRenderHeader?.(data, colIndex, column, rowIndex);
  const textContent = cellProps?.onRenderText?.(data, colIndex, column, rowIndex);

  return (
    <div
      className="flex flex-col justify-center px-3 py-2 text-sm text-gray-700 min-w-0"
      style={{
        minWidth: column.minWidth,
        maxWidth: column.maxWidth,
        flex: column.maxWidth ? `0 0 ${column.maxWidth}px` : `1 1 ${column.minWidth || 100}px`,
        ...cellProps?.styles?.root,
      }}
    >
      {headerContent ??
        (headerValue ? (
          <span className="text-sm font-medium text-text-primary truncate">{String(headerValue)}</span>
        ) : null)}
      {textContent ??
        (textValue !== undefined && textValue !== null && textValue !== '' ? (
          <span className="text-sm text-gray-600 truncate">{String(textValue)}</span>
        ) : cellProps?.emptyMessage !== undefined ? (
          cellProps.emptyMessage ? (
            <span className="text-sm italic text-gray-400">{cellProps.emptyMessage}</span>
          ) : null
        ) : null)}
    </div>
  );
}

export const ListRow = React.forwardRef<HTMLDivElement, ListRowProps>(
  (
    {
      data,
      columns,
      contents,
      collapsed,
      hasOpenClose,
      hasLeftOpenClose,
      loading,
      disabled,
      className,
      style,
      onOpen,
      onClick,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(!collapsed);

    React.useEffect(() => {
      setOpen(!collapsed);
    }, [collapsed]);

    const handleToggle = React.useCallback(() => {
      if (disabled) return;
      const next = !open;
      setOpen(next);
      onOpen?.(data, next);
    }, [data, open, onOpen, disabled]);

    const isClickable = !disabled && (!!hasOpenClose || !!onClick);

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-lg border border-border bg-surface shadow-sm overflow-hidden',
          isClickable && 'cursor-pointer',
          disabled && 'opacity-60',
          className
        )}
        style={style}
        onClick={
          isClickable
            ? (e) => {
                if (hasOpenClose) handleToggle();
                onClick?.(e);
              }
            : undefined
        }
      >
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader size="sm" label="Loading..." />
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center">
              {hasOpenClose && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggle();
                  }}
                  disabled={disabled}
                  className={cn(
                    'flex h-11 shrink-0 items-center justify-center bg-transparent border-none text-gray-500 hover:text-gray-900 transition-colors duration-200',
                    hasLeftOpenClose ? 'w-9 order-first' : 'w-9 order-last',
                    disabled && 'cursor-not-allowed'
                  )}
                  aria-label={open ? 'Collapse' : 'Expand'}
                >
                  <ChevronRotate open={open}>
                    <ChevronRightIcon className="h-4 w-4" />
                  </ChevronRotate>
                </button>
              )}
              {columns.map((column, i) => (
                <ListRowCell key={column.key} column={column} data={data} colIndex={i} />
              ))}
            </div>
            {open && contents && (
              <ExpandContent className="border-t border-border/50 px-5 py-3">{contents}</ExpandContent>
            )}
          </>
        )}
      </div>
    );
  }
) as <T extends Record<string, any>>(
  props: ListRowProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;
